import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, ArrowRight, BookOpen, Calculator, Copy, Eraser, Lightbulb, Play, RotateCcw } from "lucide-react";
import { InlineMath, BlockMath } from "react-katex";
import "katex/dist/katex.min.css";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { Button } from "@/components/ui/button";
import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  solveProblem,
  toLatex,
  type ProblemType,
  type SolverResult,
} from "@/lib/math/solver";

type Bilingual = { en: string; bn: string };

const STR = {
  h1: { en: "Math Solver", bn: "ম্যাথ সলভার" },
  desc: {
    en: "Solve equations, simplify, factor, expand, differentiate and evaluate arithmetic — with step-by-step solutions. Made for SSC/HSC students.",
    bn: "সমীকরণ সমাধান, সরলীকরণ, উৎপাদক, বিস্তার, অন্তরীকরণ ও গাণিতিক হিসাব — ধাপে ধাপে সমাধান সহ। SSC/HSC শিক্ষার্থীদের জন্য।",
  },
  problemType: { en: "Problem type", bn: "সমস্যার ধরন" },
  enterProblem: { en: "Enter your problem", bn: "আপনার সমস্যা লিখুন" },
  placeholder: { en: "e.g. 2x + 5 = 13", bn: "যেমন: 2x + 5 = 13" },
  preview: { en: "Preview", bn: "প্রিভিউ" },
  solve: { en: "Solve", bn: "সমাধান করুন" },
  clear: { en: "Clear", bn: "মুছুন" },
  showSteps: { en: "Show steps", bn: "ধাপ দেখান" },
  bnDigits: { en: "Bengali numerals", bn: "বাংলা সংখ্যা" },
  examples: { en: "Try an example", bn: "উদাহরণ চেষ্টা করুন" },
  history: { en: "Recent problems", bn: "সাম্প্রতিক সমস্যা" },
  problem: { en: "Problem", bn: "সমস্যা" },
  solution: { en: "Solution", bn: "সমাধান" },
  steps: { en: "Steps", bn: "ধাপসমূহ" },
  copySolution: { en: "Copy solution", bn: "সমাধান কপি করুন" },
  examDisclaimer: {
    en: "This tool is for learning. Use it to check your work and understand the steps. Do NOT use during exams — it is against academic rules.",
    bn: "এটি শেখার জন্য। নিজের কাজ যাচাই ও ধাপ বুঝতে ব্যবহার করুন। পরীক্ষার সময় ব্যবহার করবেন না — এটি একাডেমিক নিয়মের বিরুদ্ধে।",
  },
  noSolution: {
    en: "Couldn't solve this. Try a simpler form or check our other calculators.",
    bn: "এটি সমাধান করা গেল না। সরল রূপে চেষ্টা করুন বা আমাদের অন্য ক্যালকুলেটর দেখুন।",
  },
};

const TYPE_LABELS: { value: ProblemType; en: string; bn: string }[] = [
  { value: "auto", en: "Auto-detect", bn: "অটো-ডিটেক্ট" },
  { value: "equation", en: "Solve equation", bn: "সমীকরণ" },
  { value: "quadratic", en: "Quadratic", bn: "দ্বিঘাত" },
  { value: "system", en: "System (2 vars)", bn: "সমীকরণ জোট" },
  { value: "simplify", en: "Simplify", bn: "সরলীকরণ" },
  { value: "expand", en: "Expand", bn: "বিস্তার" },
  { value: "factor", en: "Factor", bn: "উৎপাদক" },
  { value: "differentiate", en: "Differentiate", bn: "অন্তরীকরণ" },
  { value: "arithmetic", en: "Arithmetic (BODMAS)", bn: "পাটিগণিত (BODMAS)" },
];

const EXAMPLES: { en: string; bn: string; expr: string; type: ProblemType }[] = [
  { en: "SSC: 2x + 3 = 11", bn: "SSC: 2x + 3 = 11", expr: "2x + 3 = 11", type: "equation" },
  { en: "SSC: x² − 5x + 6 = 0", bn: "SSC: x² − 5x + 6 = 0", expr: "x^2 - 5x + 6 = 0", type: "quadratic" },
  { en: "HSC: 3x² + 7x − 6 = 0", bn: "HSC: 3x² + 7x − 6 = 0", expr: "3x^2 + 7x - 6 = 0", type: "quadratic" },
  { en: "Arithmetic: (15 + 8) × 4 ÷ 2", bn: "পাটিগণিত: (15 + 8) × 4 ÷ 2", expr: "(15 + 8) * 4 / 2", type: "arithmetic" },
  { en: "Expand: (x + 3)(x − 2)", bn: "বিস্তার: (x + 3)(x − 2)", expr: "(x + 3)(x - 2)", type: "expand" },
  { en: "Differentiate: x³ + 2x² − 5x + 7", bn: "অন্তরীকরণ: x³ + 2x² − 5x + 7", expr: "x^3 + 2x^2 - 5x + 7", type: "differentiate" },
  { en: "System: 2x + y = 5; x − y = 1", bn: "জোট: 2x + y = 5; x − y = 1", expr: "2x + y = 5; x - y = 1", type: "system" },
];

const KEYBOARD_KEYS: { label: string; insert: string }[] = [
  { label: "x", insert: "x" }, { label: "y", insert: "y" },
  { label: "x²", insert: "^2" }, { label: "x³", insert: "^3" }, { label: "x^n", insert: "^" },
  { label: "(", insert: "(" }, { label: ")", insert: ")" },
  { label: "+", insert: "+" }, { label: "−", insert: "-" }, { label: "×", insert: "*" }, { label: "÷", insert: "/" },
  { label: "=", insert: "=" }, { label: ";", insert: "; " },
  { label: "√", insert: "sqrt(" }, { label: "π", insert: "pi" },
];

const FAQ: { q: Bilingual; a: Bilingual }[] = [
  {
    q: { en: "How does the math solver work?", bn: "ম্যাথ সলভার কীভাবে কাজ করে?" },
    a: {
      en: "It parses your math expression, detects the problem type (equation, quadratic, system, simplification, derivative, arithmetic), then applies the standard solving steps and shows each transformation. Calculations use the open-source mathjs library plus custom step generators for human-readable explanations.",
      bn: "এটি আপনার রাশি বিশ্লেষণ করে সমস্যার ধরন (সমীকরণ, দ্বিঘাত, জোট, সরলীকরণ, অন্তরজ, পাটিগণিত) চিহ্নিত করে, এরপর আদর্শ সমাধানের ধাপ প্রয়োগ করে প্রতিটি রূপান্তর দেখায়। হিসাবের জন্য open-source mathjs লাইব্রেরি ব্যবহৃত হয়।",
    },
  },
  {
    q: { en: "Can it solve any equation?", bn: "এটি কি যেকোনো সমীকরণ সমাধান করতে পারে?" },
    a: {
      en: "It handles linear and quadratic equations in one variable, systems of two linear equations, polynomial expansion/simplification, basic differentiation, and arithmetic. Higher-order polynomials, transcendental equations, and integrals beyond simple cases are not supported here — try our Integral Calculator for those.",
      bn: "এটি এক চলকের রৈখিক ও দ্বিঘাত সমীকরণ, দুই চলকের রৈখিক জোট, বহুপদীর বিস্তার/সরলীকরণ, প্রাথমিক অন্তরীকরণ ও পাটিগণিত করতে পারে। উচ্চ ক্রমের বহুপদী, ত্রিকোণমিতিক সমীকরণ ও জটিল সমাকলনের জন্য আমাদের Integral Calculator দেখুন।",
    },
  },
  {
    q: { en: "How accurate is it?", bn: "এটি কতটা নির্ভুল?" },
    a: {
      en: "Symbolic answers (e.g. x = 4) are exact when the inputs are exact. Numerical answers use IEEE 754 double precision. For very large numbers or special functions, expect rounding at the 14th–16th significant digit.",
      bn: "প্রতীকী উত্তর (যেমন x = 4) ইনপুট সঠিক হলে সঠিক। সংখ্যাত্মক উত্তরে IEEE 754 ডাবল প্রিসিশন ব্যবহৃত হয় — ১৪-১৬ অঙ্কের পরে রাউন্ডিং হতে পারে।",
    },
  },
  {
    q: { en: "Can I use this during an exam?", bn: "এটি কি পরীক্ষার সময় ব্যবহার করা যাবে?" },
    a: {
      en: "No. This is a learning tool. Using it during an exam violates academic integrity rules and your school/board policies. Use it to study, check your work, and understand the steps you missed.",
      bn: "না। এটি শেখার সরঞ্জাম। পরীক্ষার সময় ব্যবহার একাডেমিক সততার নিয়ম ও আপনার বোর্ডের নীতি লঙ্ঘন করে। পড়াশোনা ও কাজ যাচাইয়ের জন্য ব্যবহার করুন।",
    },
  },
  {
    q: { en: "How do I type math expressions?", bn: "ম্যাথ এক্সপ্রেশন কীভাবে লিখব?" },
    a: {
      en: "Type naturally: 2x + 5 = 13. Use ^ for powers (x^2), * for explicit multiplication, sqrt() for roots, pi for π. Use the on-screen keyboard for quick symbols. Separate two equations of a system with a semicolon.",
      bn: "স্বাভাবিকভাবে লিখুন: 2x + 5 = 13। শক্তির জন্য ^ (x^2), গুণের জন্য *, মূলের জন্য sqrt(), π এর জন্য pi। দ্রুত প্রতীকের জন্য অন-স্ক্রিন কীবোর্ড ব্যবহার করুন। জোট সমীকরণ সেমিকোলন দিয়ে আলাদা করুন।",
    },
  },
  {
    q: { en: "Why does it show steps?", bn: "ধাপ কেন দেখায়?" },
    a: {
      en: "Showing steps is the whole point — copying just the answer doesn't teach you anything. Read each step, understand the rule applied (distributive law, power rule, etc.), then try the next problem yourself before checking.",
      bn: "ধাপ দেখানোই মূল কথা — শুধু উত্তর কপি করলে কিছু শেখা যায় না। প্রতিটি ধাপ পড়ে প্রয়োগ করা নিয়ম (বিতরণ সূত্র, পাওয়ার রুল ইত্যাদি) বুঝুন, তারপর নিজে পরের সমস্যা চেষ্টা করুন।",
    },
  },
  {
    q: { en: "ম্যাথ সলভার কীভাবে কাজ করে?", bn: "ম্যাথ সলভার কীভাবে কাজ করে?" },
    a: {
      en: "আপনার রাশি লিখুন, সমস্যার ধরন বেছে নিন (অথবা অটো-ডিটেক্ট), 'সমাধান' চাপুন। প্রতিটি ধাপ ও সূত্র দেখানো হবে।",
      bn: "আপনার রাশি লিখুন, সমস্যার ধরন বেছে নিন (অথবা অটো-ডিটেক্ট), 'সমাধান' চাপুন। প্রতিটি ধাপ ও সূত্র দেখানো হবে।",
    },
  },
  {
    q: { en: "এসএসসি গণিত সমস্যার সমাধান?", bn: "এসএসসি গণিত সমস্যার সমাধান?" },
    a: {
      en: "হ্যাঁ — এই টুল SSC স্তরের রৈখিক ও দ্বিঘাত সমীকরণ, সমীকরণ জোট, সরলীকরণ ও বিস্তারের জন্য ভালো কাজ করে। উদাহরণ গ্যালারি থেকে শুরু করুন।",
      bn: "হ্যাঁ — এই টুল SSC স্তরের রৈখিক ও দ্বিঘাত সমীকরণ, সমীকরণ জোট, সরলীকরণ ও বিস্তারের জন্য ভালো কাজ করে। উদাহরণ গ্যালারি থেকে শুরু করুন।",
    },
  },
];

const HISTORY_KEY = "blcalc:math-history";

export const Route = createFileRoute("/math-solver")({
  head: () => {
    const seo = buildSeo({
      title: "Math Solver — Step-by-Step Equation Solver",
      description:
        "Free math solver with step-by-step solutions. Solve equations, factor, simplify, differentiate. Made for SSC/HSC students in Bangladesh. ম্যাথ সলভার।",
      path: "/math-solver",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Math Solver", path: "/math-solver" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: MathSolverPage,
});

function MathSolverPage() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof STR) => (STR[k] as Bilingual)[lang];

  const [type, setType] = useState<ProblemType>("auto");
  const [input, setInput] = useState("2x + 5 = 13");
  const [showSteps, setShowSteps] = useState(true);
  const [bnDigits, setBnDigits] = useState(false);
  const [result, setResult] = useState<SolverResult | null>(null);
  const [history, setHistory] = useState<string[]>([]);

  // Load history
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(HISTORY_KEY);
      if (raw) setHistory(JSON.parse(raw));
    } catch {}
  }, []);

  const previewTex = useMemo(() => {
    try {
      return toLatex(input.replace(/;\s*/, ", "));
    } catch {
      return input;
    }
  }, [input]);

  const localizeNumerals = (s: string) => (bnDigits ? toBengaliNumerals(s) : s);

  const onSolve = () => {
    const r = solveProblem(input, { type });
    setResult(r);
    if (r.ok && typeof window !== "undefined") {
      const next = [input, ...history.filter((h) => h !== input)].slice(0, 10);
      setHistory(next);
      try { window.localStorage.setItem(HISTORY_KEY, JSON.stringify(next)); } catch {}
    }
  };

  const onClear = () => {
    setInput("");
    setResult(null);
  };

  const insertAtCaret = (frag: string) => {
    setInput((s) => s + frag);
  };

  const copySolution = () => {
    if (!result?.ok) return;
    const lines = [
      `${tr("problem")}: ${result.problem}`,
      `${tr("solution")}: ${result.answer}`,
      "",
      `${tr("steps")}:`,
      ...result.steps.map((s, i) => `${i + 1}. ${s[lang]}`),
    ];
    if (typeof navigator !== "undefined") {
      navigator.clipboard?.writeText(lines.join("\n"));
    }
  };

  return (
    <CalculatorLayout
      slug="math-solver"
      title={tr("h1")}
      description={tr("desc")}
      iconName="Calculator"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      {/* Exam disclaimer (above the fold) */}
      <div className={cn(
        "mb-5 flex items-start gap-3 rounded-xl border border-yellow-400/40 bg-yellow-400/10 p-3 text-xs text-foreground/90",
        lang === "bn" && "font-bn",
      )}>
        <AlertTriangle size={16} className="mt-0.5 shrink-0 text-yellow-500" />
        <p>{tr("examDisclaimer")}</p>
      </div>

      {/* Problem type chips */}
      <div className="space-y-1.5">
        <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>{tr("problemType")}</label>
        <div className="flex flex-wrap gap-1.5">
          {TYPE_LABELS.map((t) => {
            const active = t.value === type;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => setType(t.value)}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs font-medium transition",
                  active
                    ? "border-accent bg-accent text-accent-foreground"
                    : "border-border bg-card text-muted-foreground hover:bg-muted",
                  lang === "bn" && "font-bn",
                )}
              >
                {lang === "bn" ? t.bn : t.en}
              </button>
            );
          })}
        </div>
      </div>

      {/* Input + preview */}
      <div className="mt-5 space-y-2">
        <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>{tr("enterProblem")}</label>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={tr("placeholder")}
          rows={2}
          className="w-full rounded-lg border border-border bg-background px-3 py-2 font-mono text-base focus:border-accent focus:outline-none"
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) onSolve();
          }}
        />
        {input.trim() && (
          <div className="rounded-lg border border-border bg-muted/30 px-3 py-2">
            <p className={cn("mb-1 text-[10px] uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>{tr("preview")}</p>
            <div className="overflow-x-auto text-foreground">
              <BlockMath math={previewTex} />
            </div>
          </div>
        )}
      </div>

      {/* On-screen math keyboard */}
      <div className="mt-3 flex flex-wrap gap-1.5">
        {KEYBOARD_KEYS.map((k) => (
          <button
            key={k.label}
            type="button"
            onClick={() => insertAtCaret(k.insert)}
            className="min-w-[36px] rounded-md border border-border bg-card px-2 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
          >
            {k.label}
          </button>
        ))}
      </div>

      {/* Toggles + actions */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button onClick={onSolve} className="gap-2">
          <Play size={14} />
          {tr("solve")}
        </Button>
        <Button variant="outline" onClick={onClear} className="gap-2">
          <Eraser size={14} />
          {tr("clear")}
        </Button>
        <label className={cn("ml-auto inline-flex items-center gap-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          <input type="checkbox" checked={showSteps} onChange={(e) => setShowSteps(e.target.checked)} className="h-3.5 w-3.5 accent-accent" />
          {tr("showSteps")}
        </label>
        <label className={cn("inline-flex items-center gap-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          <input type="checkbox" checked={bnDigits} onChange={(e) => setBnDigits(e.target.checked)} className="h-3.5 w-3.5 accent-accent" />
          {tr("bnDigits")}
        </label>
      </div>

      {/* Result */}
      <AnimatePresence mode="wait">
        {result && (
          <motion.div
            key={result.problem + (result.answer ?? "x")}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="mt-6 space-y-4"
          >
            {!result.ok && (
              <div className={cn("rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive", lang === "bn" && "font-bn")}>
                {result.error?.[lang] ?? tr("noSolution")}
              </div>
            )}
            {result.ok && (
              <>
                <div className="rounded-xl border border-accent/30 bg-accent/5 p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className={cn("text-xs font-semibold uppercase tracking-wide text-accent", lang === "bn" && "font-bn")}>
                        {tr("solution")}
                      </p>
                      <div className="mt-1 overflow-x-auto">
                        <BlockMath math={localizeNumerals(result.answerTex ?? result.answer ?? "")} />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={copySolution}
                      className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
                    >
                      <Copy size={12} /> {tr("copySolution")}
                    </button>
                  </div>
                </div>

                {showSteps && result.steps.length > 0 && (
                  <div className="rounded-xl border border-border bg-card p-5">
                    <h3 className={cn("mb-3 text-sm font-bold", lang === "bn" && "font-bn")}>{tr("steps")}</h3>
                    <ol className="space-y-3">
                      {result.steps.map((s, i) => (
                        <li key={i} className="rounded-lg border border-border/50 bg-muted/20 p-3">
                          <div className="flex items-start gap-3">
                            <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent/15 text-xs font-semibold text-accent tabular-nums">
                              {bnDigits ? toBengaliNumerals(i + 1) : i + 1}
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className={cn("text-sm text-foreground", lang === "bn" && "font-bn")}>{s[lang]}</p>
                              {s.expr && (
                                <div className="mt-1.5 overflow-x-auto text-sm">
                                  <InlineMath math={localizeNumerals(toLatex(s.expr.replace(/;\s*/, ", ")))} />
                                </div>
                              )}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Examples gallery */}
      <section className="mt-8 rounded-xl border border-border bg-card p-4">
        <h2 className={cn("mb-3 flex items-center gap-2 text-sm font-bold", lang === "bn" && "font-bn")}>
          <Lightbulb size={16} className="text-accent" />
          {tr("examples")}
        </h2>
        <div className="grid gap-2 sm:grid-cols-2">
          {EXAMPLES.map((ex) => (
            <button
              key={ex.expr}
              type="button"
              onClick={() => { setInput(ex.expr); setType(ex.type); setResult(null); }}
              className={cn(
                "rounded-lg border border-border bg-background px-3 py-2 text-left text-xs font-medium text-foreground hover:border-accent hover:bg-accent/5",
                lang === "bn" && "font-bn",
              )}
            >
              {lang === "bn" ? ex.bn : ex.en}
            </button>
          ))}
        </div>
      </section>

      {/* History */}
      {history.length > 0 && (
        <section className="mt-6 rounded-xl border border-border bg-card p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className={cn("flex items-center gap-2 text-sm font-bold", lang === "bn" && "font-bn")}>
              <RotateCcw size={14} className="text-muted-foreground" />
              {tr("history")}
            </h2>
            <button
              type="button"
              onClick={() => { setHistory([]); try { window.localStorage.removeItem(HISTORY_KEY); } catch {} }}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              ×
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {history.map((h, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setInput(h)}
                className="rounded-md border border-border bg-background px-2 py-1 font-mono text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                {h.length > 40 ? h.slice(0, 40) + "…" : h}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Cross-links */}
      <div className="mt-6 flex flex-wrap gap-3 text-xs">
        <Link to="/scientific-calculator" className="inline-flex items-center gap-1 text-accent hover:underline">Scientific Calculator <ArrowRight size={12} /></Link>
        <Link to="/percentage-calculator" className="inline-flex items-center gap-1 text-accent hover:underline">Percentage <ArrowRight size={12} /></Link>
      </div>

      {/* Long-form content */}
      <article className="mt-12 space-y-10">
        <Section title={lang === "bn" ? "ম্যাথ সলভার কীভাবে ব্যবহার করবেন" : "How to use the math solver"} lang={lang}>
          <ol className={cn("list-decimal space-y-1 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{lang === "bn" ? "সমস্যার ধরন বেছে নিন (অথবা 'অটো-ডিটেক্ট' রাখুন)।" : "Pick the problem type (or leave it on 'Auto-detect')."}</li>
            <li>{lang === "bn" ? "টেক্সট বক্সে রাশিটি লিখুন। প্রিভিউতে গণিত ফরম্যাটে রেন্ডার হবে।" : "Type your expression in the box. The preview renders it in math format."}</li>
            <li>{lang === "bn" ? "'সমাধান' চাপুন — উত্তর ও ধাপগুলো নিচে দেখাবে।" : "Hit 'Solve' — the answer and steps appear below."}</li>
            <li>{lang === "bn" ? "প্রতিটি ধাপ পড়ুন, কোন নিয়ম প্রয়োগ হলো বুঝুন, তারপর নিজে পরের সমস্যা চেষ্টা করুন।" : "Read each step, understand the rule applied, then try the next problem yourself."}</li>
          </ol>
        </Section>

        <Section title={lang === "bn" ? "সমর্থিত সমস্যার ধরন" : "Supported problem types"} lang={lang}>
          <ul className={cn("list-disc space-y-1 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{lang === "bn" ? "এক চলকের রৈখিক সমীকরণ" : "Linear equations in one variable"}</li>
            <li>{lang === "bn" ? "দ্বিঘাত সমীকরণ (নির্ণায়ক ও দ্বিঘাত সূত্র)" : "Quadratic equations (discriminant + quadratic formula)"}</li>
            <li>{lang === "bn" ? "দুই চলকের রৈখিক জোট (অপনয়ন পদ্ধতি)" : "Systems of two linear equations (elimination method)"}</li>
            <li>{lang === "bn" ? "বহুপদীর সরলীকরণ, বিস্তার ও আংশিক উৎপাদক" : "Polynomial simplification, expansion and partial factoring"}</li>
            <li>{lang === "bn" ? "প্রাথমিক অন্তরীকরণ (পাওয়ার, যোগ ও ধ্রুবক নিয়ম)" : "Basic differentiation (power, sum and constant rules)"}</li>
            <li>{lang === "bn" ? "BODMAS সহ পাটিগণিতিক রাশি" : "Arithmetic expressions with BODMAS / order of operations"}</li>
          </ul>
        </Section>

        <Section title="BODMAS / PEMDAS" lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "ক্রম: বন্ধনী → শক্তি/মূল → গুণ ও ভাগ (বাম থেকে ডান) → যোগ ও বিয়োগ (বাম থেকে ডান)। উদাহরণ: (১৫ + ৮) × ৪ ÷ ২ = ২৩ × ৪ ÷ ২ = ৯২ ÷ ২ = ৪৬।"
              : "Order: Brackets → Powers / Roots → Multiplication & Division (left-to-right) → Addition & Subtraction (left-to-right). Example: (15 + 8) × 4 ÷ 2 = 23 × 4 ÷ 2 = 92 ÷ 2 = 46."}
          </p>
        </Section>

        <Section title={lang === "bn" ? "রৈখিক সমীকরণ — ম্যানুয়াল পদ্ধতি" : "Linear equation — manual method"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "ax + b = cx + d রূপের সমীকরণে: (১) সকল x-পদ এক পাশে নিন, (২) ধ্রুবকগুলো অন্য পাশে, (৩) দু-পাশকে x এর সহগ দিয়ে ভাগ করুন।"
              : "For ax + b = cx + d: (1) move x-terms to one side, (2) constants to the other, (3) divide both sides by the coefficient of x."}
          </p>
        </Section>

        <Section title={lang === "bn" ? "দ্বিঘাত সূত্র" : "Quadratic formula"} lang={lang}>
          <div className="overflow-x-auto rounded-lg border border-border bg-muted/30 p-3">
            <BlockMath math={"x = \\frac{-b \\pm \\sqrt{b^{2} - 4ac}}{2a}"} />
          </div>
          <p className={cn("mt-2 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "যেখানে a, b, c হলো ax² + bx + c = 0 এর সহগ। নির্ণায়ক D = b² − 4ac এর চিহ্ন বলে দেয় মূল কয়টি — D > 0 দুটি বাস্তব মূল, D = 0 একটি দ্বিগুণ মূল, D < 0 দুটি জটিল মূল।"
              : "where a, b, c are the coefficients of ax² + bx + c = 0. The sign of the discriminant D = b² − 4ac tells you the nature of the roots — D > 0 two real roots, D = 0 one double root, D < 0 two complex roots."}
          </p>
        </Section>

        <Section title={lang === "bn" ? "ধাপ দেখা কেন গুরুত্বপূর্ণ" : "Why showing steps matters"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "শুধু উত্তর কপি করলে শেখা হয় না — পরীক্ষায়ও আপনি একই সমস্যা সমাধান করতে পারবেন না। প্রতিটি ধাপ পড়ুন, প্রয়োগ করা নিয়ম বুঝুন (বিতরণ সূত্র, পাওয়ার রুল, অপনয়ন), তারপর নিজে চেষ্টা করুন। সলভার শুধু একটি যাচাই-সরঞ্জাম।"
              : "Copying just the answer doesn't teach you anything — and in the exam, you won't be able to solve a similar problem on your own. Read each step, understand the rule applied (distributive law, power rule, elimination), then practise. The solver is a check-tool, not a substitute for practice."}
          </p>
        </Section>

        <Section title={lang === "bn" ? "দাবিত্যাগ" : "Disclaimer"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>{tr("examDisclaimer")}</p>
        </Section>
      </article>
    </CalculatorLayout>
  );
}

function Section({ title, children, lang }: { title: string; children: React.ReactNode; lang: "en" | "bn" }) {
  return (
    <section>
      <h2 className={cn("mb-3 text-lg font-bold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      {children}
    </section>
  );
}
